import React, { useState, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, Navigation, EffectFade } from 'swiper/modules';
import { Swiper as SwiperType } from 'swiper';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { heroSlidesService, HeroSlide } from '../services/heroSlidesService';
import { getImageUrl } from '../services/api';

// Import Swiper styles
import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import 'swiper/css/effect-fade';

// Default fallback slides if API returns empty
const defaultSlides: HeroSlide[] = [
    {
        id: 1,
        image: "https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?q=80&w=2070&auto=format&fit=crop",
        subtitle: "Innovation Pharmaceutique",
        title: "L'Excellence au Service de la Santé",
        description: "SAPI s'engage à fournir des solutions pharmaceutiques de haute qualité, répondant aux standards internationaux les plus stricts.",
        ctaText: "Découvrir Nos Produits",
        ctaLink: "/produits",
        order: 1
    }
];

const HeroSlider: React.FC = () => {
    const [activeIndex, setActiveIndex] = useState(0);
    const [slides, setSlides] = useState<HeroSlide[]>([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchSlides = async () => {
            try {
                const data = await heroSlidesService.getAll();
                if (data && data.length > 0) {
                    setSlides(data);
                } else {
                    setSlides(defaultSlides);
                }
            } catch (error) {
                console.error("Failed to fetch hero slides:", error);
                setSlides(defaultSlides);
            } finally {
                setLoading(false);
            }
        };

        fetchSlides();
    }, []);


    const slideVariants = {
        hidden: { opacity: 0, scale: 0.8 },
        visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } },
        exit: { opacity: 0, scale: 0.8, transition: { duration: 0.3 } }
    };

    const textVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: (custom: number) => ({
            opacity: 1,
            y: 0,
            transition: {
                delay: custom * 0.2,
                duration: 0.8,
                ease: "easeOut" as any
            }
        })
    };

    if (loading) {
        return <div className="h-[90vh] bg-gray-900 flex items-center justify-center text-white">Loading...</div>;
    }

    return (
        <div className="relative w-full h-[90vh] min-h-[600px] bg-gray-900 overflow-hidden group">
            <Swiper
                modules={[Autoplay, Pagination, Navigation, EffectFade]}
                spaceBetween={0}
                slidesPerView={1}
                effect={'fade'}
                speed={1000}
                autoplay={{
                    delay: 6000,
                    disableOnInteraction: false,
                }}
                pagination={{
                    clickable: true,
                    renderBullet: function (index: number, className: string) {
                        return '<span class="' + className + ' !bg-white !opacity-50 hover:!opacity-100 !w-3 !h-3 !transition-all duration-300"></span>';
                    },
                }}
                navigation={{
                    prevEl: '.swiper-button-prev-custom',
                    nextEl: '.swiper-button-next-custom',
                }}
                onSlideChange={(swiper: SwiperType) => setActiveIndex(swiper.realIndex)}
                className="w-full h-full"
            >
                {slides.map((slide, index) => (
                    <SwiperSlide key={slide.id} className="relative w-full h-full">
                        {/* Background Image with Overlay */}
                        <div className="absolute inset-0 w-full h-full">
                            <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent z-10" />
                            <img
                                src={getImageUrl(slide.image)}
                                alt={slide.title}
                                className="w-full h-full object-cover animate-pan-zoom"
                                style={{
                                    animation: activeIndex === index ? 'panZoom 20s infinite linear' : 'none'
                                }}
                            />
                        </div>

                        {/* Content Container */}
                        <div className="absolute inset-0 z-20 flex items-center">
                            <div className="container mx-auto px-6 md:px-12 lg:px-20">
                                <div className="max-w-3xl text-white">

                                    {/* We use AnimatePresence key-ed by index to re-trigger animations */}
                                    {activeIndex === index && (
                                        <motion.div
                                            initial="hidden"
                                            animate="visible"
                                            className="space-y-6"
                                        >
                                            <motion.div
                                                custom={0}
                                                variants={textVariants}
                                                className="inline-block px-4 py-1.5 border border-primary-light/30 bg-primary/20 backdrop-blur-sm rounded-full"
                                            >
                                                <span className="text-sm md:text-base font-medium text-primary-light uppercase tracking-wider">
                                                    {slide.subtitle}
                                                </span>
                                            </motion.div>

                                            <motion.h1
                                                custom={1}
                                                variants={textVariants}
                                                className="text-4xl md:text-6xl lg:text-7xl font-bold leading-tight"
                                            >
                                                {slide.title}
                                            </motion.h1>

                                            <motion.p
                                                custom={2}
                                                variants={textVariants}
                                                className="text-lg md:text-xl text-gray-200 max-w-xl leading-relaxed"
                                            >
                                                {slide.description}
                                            </motion.p>

                                            <motion.div
                                                custom={3}
                                                variants={textVariants}
                                                className="pt-4"
                                            >
                                                <Link
                                                    to={slide.ctaLink}
                                                    className="group relative inline-flex items-center gap-3 px-8 py-4 bg-primary text-white rounded-full overflow-hidden transition-all duration-300 hover:bg-primary-dark hover:shadow-lg hover:shadow-primary/25"
                                                >
                                                    <span className="relative z-10 font-semibold text-lg tracking-wide">{slide.ctaText}</span>
                                                    <ArrowRight className="w-5 h-5 relative z-10 transition-transform duration-300 group-hover:translate-x-1" />
                                                    <div className="absolute inset-0 bg-gradient-to-r from-primary-light to-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                                </Link>
                                            </motion.div>
                                        </motion.div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </SwiperSlide>
                ))}

                {/* Custom Navigation Buttons */}
                <div className="hidden lg:flex absolute bottom-12 right-20 z-30 gap-4">
                    <button className="swiper-button-prev-custom w-14 h-14 flex items-center justify-center rounded-full border border-white/20 bg-white/5 backdrop-blur-sm text-white transition-all hover:bg-white hover:text-primary hover:scale-110 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed">
                        <ChevronLeft size={24} />
                    </button>
                    <button className="swiper-button-next-custom w-14 h-14 flex items-center justify-center rounded-full border border-white/20 bg-white/5 backdrop-blur-sm text-white transition-all hover:bg-white hover:text-primary hover:scale-110 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed">
                        <ChevronRight size={24} />
                    </button>
                </div>

            </Swiper>

            <style>{`
        @keyframes panZoom {
            0% { transform: scale(1); }
            100% { transform: scale(1.1); }
        }
        .swiper-pagination-bullet-active {
            opacity: 1 !important;
            background-color: var(--color-primary) !important;
            width: 24px !important;
        }
      `}</style>
        </div>
    );
};

export default HeroSlider;
